import { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { clampAmount } from "../lib/amount";
import { useUnits } from "../lib/useUnits";
import { gToOz, ozToG, mlToFloz, flozToMl } from "@shared/units";
import { hapticTap, hapticSelect } from "../lib/haptics";

interface Props {
  value: number;
  unit: "g" | "ml";
  onChange: (amount: number, unit: "g" | "ml") => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  "aria-label"?: string;
}

/** Reusable weight editor: [−] number [+] with a g·ml pill. Keeps an internal
 *  text buffer so the field can be cleared/retyped; emits a clamped integer on
 *  each valid keystroke and normalizes the text on blur. Unit is a label only. */
export function WeightStepper({
  value, unit, onChange, min = 1, max = 5000, step = 10, disabled = false,
  "aria-label": ariaLabel,
}: Props) {
  const t = useT();
  // Imperial serving pref: SHOW oz / fl oz, keep g / ml canonical underneath.
  const units = useUnits();
  const imp = units.prefs.serving === "imperial";
  const toDisp = (v: number) => (imp ? Math.round((unit === "g" ? gToOz(v) : mlToFloz(v)) * 10) / 10 : v);
  const fromDisp = (n: number) => (imp ? (unit === "g" ? ozToG(n) : flozToMl(n)) : n);
  const [text, setText] = useState(String(toDisp(value)));
  useEffect(() => { setText(String(toDisp(value))); }, [value, imp]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (amount: number) => onChange(amount, unit);
  const onText = (raw: string) => {
    setText(raw);
    const n = parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(n)) return;
    const canonical = Math.round(fromDisp(n));
    if (canonical >= min && canonical <= max) emit(canonical);
  };
  const onBlur = () => {
    const n = parseFloat(text.replace(",", "."));
    const canonical = clampAmount(Number.isFinite(n) ? String(Math.round(fromDisp(n))) : "", min, max);
    setText(String(toDisp(canonical)));
    emit(canonical);
  };

  const btn = "w-11 h-11 rounded-full bg-surface-strong text-fg text-xl leading-none press disabled:opacity-40";
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" disabled={disabled || value <= min}
        onClick={() => { hapticTap(); emit(clampAmount(value - step, min, max)); }}
        aria-label={t("weight.amount_decrease")} className={btn}>−</button>
      <input
        type="number" inputMode="decimal" min={min} max={max} disabled={disabled}
        value={text} onChange={(e) => onText(e.target.value)} onBlur={onBlur}
        aria-label={ariaLabel ?? t("weight.amount_label")}
        className="w-14 bg-surface-strong rounded-lg px-1 py-1.5 text-center text-sm tabular-nums"
      />
      <button type="button" disabled={disabled || value >= max}
        onClick={() => { hapticTap(); emit(clampAmount(value + step, min, max)); }}
        aria-label={t("weight.amount_increase")} className={btn}>+</button>
      <div role="group" aria-label={t("weight.unit_label")}
        className="flex rounded-xl overflow-hidden bg-surface text-sm ml-0.5">
        <button type="button" disabled={disabled} onClick={() => { hapticSelect(); onChange(value, "g"); }}
          aria-pressed={unit === "g"}
          className={`px-3 min-h-[44px] ${unit === "g" ? "bg-surface-strong text-fg" : "text-fg-muted"}`}>{imp ? "oz" : "g"}</button>
        <button type="button" disabled={disabled} onClick={() => { hapticSelect(); onChange(value, "ml"); }}
          aria-pressed={unit === "ml"}
          className={`px-3 min-h-[44px] ${unit === "ml" ? "bg-surface-strong text-fg" : "text-fg-muted"}`}>{imp ? "fl oz" : "ml"}</button>
      </div>
    </div>
  );
}
